// @ts-nocheck

/**
 * The Emarsys custom pixel events bridge.
 *
 * Cart flow: hook fetch + XHR -> match /cart/(add|change|update|clear)
 *       -> debounce 150ms -> GET {root}cart.js
 *       -> Shopify.analytics.publish('cart_updated', { cart }).
 *
 * Page context (FNF0004-1677): Liquid-derived category path / search term, once per load
 *       -> Shopify.analytics.publish('fh_category_viewed' | 'fh_search_viewed').
 */
(function () {

  if (window.__emarsysCartEventsInstalled) return;
  window.__emarsysCartEventsInstalled = true;

  const DEBUG = true; // GO-LIVE: set false.
  const DEBOUNCE_MS = 150;
  const READY_POLL_MS = 100;
  const READY_TIMEOUT_MS = 5000;

  // Matches /cart/add, /cart/change, /cart/update, /cart/clear
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(?:[/?#]|$)/i;

  const nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  let timer = null;
  let lastSignature = null;

  function log() {
    if (!DEBUG || !window.console || !console.log) return;
    try {
      console.log.apply(console, ['[emarsys-cart-events]'].concat([].slice.call(arguments)));
    } catch {
      console.log('console unavailable - ignore');
      /* console unavailable - ignore */
    }
  }

  function urlFromInput(input) {
    if (typeof input === 'string') return input;
    if (!input) return '';
    return input.url || input.href || '';
  }

  function isCartMutation(url) {
    return typeof url === 'string' && CART_MUTATION_RE.test(url);
  }

  function rootUrl() {
    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      (window.Theme && Theme.routes && Theme.routes.root) ||
      '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function scheduleUpdate() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(publishCartUpdate, DEBOUNCE_MS);
  }

  function cartSignature(cart) {
    const lines = (cart.items || [])
      .map(function (item) {
        return (item.key || item.id) + ':' + item.quantity + ':' + item.final_line_price;
      })
      .join(',');
    return cart.item_count + '|' + cart.total_price + '|' + lines;
  }

  function publishCartUpdate() {
    timer = null;

    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') {
      log('Shopify.analytics not ready - skipping publish');
      return;
    }
    if (!nativeFetch) return;

    nativeFetch(rootUrl() + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) {
          log('cart.js returned an unexpected shape', cart);
          return;
        }
        const signature = cartSignature(cart);
        if (signature === lastSignature) {
          log('cart unchanged - skipping duplicate publish');
          return;
        }
        lastSignature = signature;
        Shopify.analytics.publish('cart_updated', { cart: cart });
        log('published cart_updated', cart.item_count, cart.currency);
      })
      .catch(function (error) {
        log('cart.js read failed', error);
      });
  }

  function analyticsReady() {
    return !!(
      window.Shopify &&
      Shopify.analytics &&
      typeof Shopify.analytics.publish === 'function'
    );
  }

  /* Page context fires once, so a late Shopify.analytics loses it permanently - poll. */
  function whenAnalyticsReady(callback) {
    if (analyticsReady()) {
      callback();
      return;
    }

    const startedAt = Date.now();
    const poll = setInterval(function () {
      if (analyticsReady()) {
        clearInterval(poll);
        callback();
        return;
      }
      if (Date.now() - startedAt >= READY_TIMEOUT_MS) {
        clearInterval(poll);
        log('Shopify.analytics never became ready - page context not published');
      }
    }, READY_POLL_MS);
  }

  /* collection_viewed carries one title node and FH only a `catalog01_<id>` token -
   * neither gives Emarsys the `>` separated path, so the theme publishes it. */
  function publishPageContext() {
    if (typeof Theme === 'undefined') return;

    const currencyCode =
      (Theme.localization && Theme.localization.market && Theme.localization.market.currencyCode) ||
      '';
    const categoryPath = (Theme.collection && Theme.collection.categoryPath) || '';
    const searchPerformed = !!(Theme.search && Theme.search.performed);
    const searchTerm = (Theme.search && Theme.search.terms) || '';

    // The values are the template guard; `Theme.template.name` is not - it carries
    // the suffix ("collection.custom").
    try {
      if (categoryPath) {
        Shopify.analytics.publish('fh_category_viewed', {
          category: categoryPath,
          currencyCode: currencyCode,
        });
        log('published fh_category_viewed', categoryPath);
        return;
      }

      if (searchPerformed && searchTerm) {
        Shopify.analytics.publish('fh_search_viewed', {
          searchTerm: searchTerm,
          currencyCode: currencyCode,
        });
        log('published fh_search_viewed', searchTerm);
      }
    } catch (error) {
      log('page context publish failed', error);
    }
  }

  // --- fetch hook ---
  if (nativeFetch) {
    window.fetch = function (input, init) {
      const promise = nativeFetch(input, init);
      if (isCartMutation(urlFromInput(input))) {
        promise.then(
          function (response) {
            if (response && response.ok) scheduleUpdate();
          },
          function () {
            /* request failed - no cart change to report */
          },
        );
      }
      return promise;
    };
  }

  // --- XHR hook ---
  const XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const nativeOpen = XHR.prototype.open;
    const nativeSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      this.__emarsysCartMutation = isCartMutation(url);
      return nativeOpen.apply(this, arguments);
    };

    XHR.prototype.send = function () {
      if (this.__emarsysCartMutation) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) scheduleUpdate();
        });
      }
      return nativeSend.apply(this, arguments);
    };
  }

  document.addEventListener('cart:update', scheduleUpdate);

  whenAnalyticsReady(publishPageContext);

  log('installed');
})();
